<template>
  <div class="configurations-table-header">
    <div class="configurations-table-header__top-row">
      <span class="configurations-table-header__title">Configurations</span>
      <mi-separator vertical class="q-mx-sm"></mi-separator>
      <mi-btn
        flat
        dense
        round
        class="configurations-table-header__pin-filter"
        :class="{ 'configurations-table-header__pin-filter--active': isPinFilterActive }"
        @click="$emit('toggle-pin-filter')"
      >
        <img
          :src="isPinFilterActive
            ? require('@/assets/images/pin-dark.svg')
            : require('@/assets/images/pin-outline.svg')"
          alt="pin filter"
          width="16"
          height="16"
        />
      </mi-btn>
      <mi-separator vertical class="q-mx-sm"></mi-separator>
      <mi-text-field
        :model-value="configurationsSearchString"
        bg-color="white"
        debounce="500"
        placeholder="Search"
        clearable
        hide-bottom-space
        outlined
        class="configurations-table-header__search"
        @update:model-value="$emit('search-input', $event)"
      >
        <template #prepend>
          <mi-icon name="search"></mi-icon>
        </template>
      </mi-text-field>
      <div class="configurations-table-header__right-actions">
        <mi-btn
          color="primary"
          icon-right="folder-multi"
          icon-right-size="14px"
          @click="$emit('navigate-saved-lists')"
        >
          Saved Lists
        </mi-btn>
        <mi-btn
          color="accent"
          icon-right="plus-circle"
          icon-right-size="14px"
          @click="$emit('new-configuration')"
        >
          New Configuration
        </mi-btn>
      </div>
    </div>
    <div class="configurations-table-header__master-checkbox">
      <div
        class="configurations-table-header__master-control"
        :class="{ 'configurations-table-header__master-dropdown--active': hasAnySelected }"
      >
        <mi-checkbox
          :model-value="masterCheckboxState"
          indeterminate-value="indeterminate"
          :toggle-indeterminate="false"
          @click="$emit('master-checkbox-click')"
        ></mi-checkbox>
        <mi-btn-dropdown
          flat
          dense
          dropdown-icon="caret-down"
          no-icon-animation
          class="configurations-table-header__master-dropdown"
          :class="{ 'configurations-table-header__master-dropdown--active': hasAnySelected }"
        >
          <div class="configurations-table-header__select-dropdown">
            <mi-btn flat @click="$emit('select-all')">
              All
            </mi-btn>

            <mi-btn flat @click="$emit('select-mine')">
              Mine only
            </mi-btn>

            <mi-btn flat @click="$emit('select-from-today')">
              From today
            </mi-btn>

            <mi-btn flat @click="$emit('select-pinned')">
              Pinned
            </mi-btn>
          </div>
        </mi-btn-dropdown>
      </div>
      <mi-separator v-if="selectedCount > 0" vertical class="q-mx-sm"></mi-separator>
      <div v-if="selectedCount > 0" class="configurations-table-header__action-buttons">
        <mi-btn
          color="primary"
          @click="$emit('create-new-list')"
        >
          Create new list
        </mi-btn>
        <mi-btn
          outline
          @click="$emit('add-to-list')"
        >
          Add to list
        </mi-btn>
      </div>
    </div>
  </div>
</template>

<script>
  import MiBtn from '@/packages/@mi-library/MiBtn/MiBtn.vue'
  import MiBtnDropdown from '@/packages/@mi-library/MiBtnDropdown/MiBtnDropdown.vue'
  import MiCheckbox from '@/packages/@mi-library/MiCheckbox/MiCheckbox.vue'
  import MiSeparator from '@/packages/@mi-library/MiSeparator/MiSeparator.vue'
  import MiTextField from '@/packages/@mi-library/MiTextField/MiTextField.vue'
  import MiIcon from '@/packages/@mi-library/MiIcon/MiIcon.vue'

  export default {
    name: 'ConfigurationsHeader',
    components: {
      MiBtn,
      MiBtnDropdown,
      MiCheckbox,
      MiSeparator,
      MiTextField,
      MiIcon
    },
    props: {
      configurationsSearchString: {
        type: String,
        default: ''
      },
      isPinFilterActive: {
        type: Boolean,
        default: false
      },
      masterCheckboxState: {
        type: [Boolean, String],
        default: false
      },
      hasAnySelected: {
        type: Boolean,
        default: false
      },
      selectedCount: {
        type: Number,
        default: 0
      }
    },
    emits: [
      'toggle-pin-filter',
      'search-input',
      'master-checkbox-click',
      'select-all',
      'select-mine',
      'select-from-today',
      'select-pinned',
      'create-new-list',
      'add-to-list',
      'navigate-saved-lists',
      'new-configuration'
    ]
  }
</script>

<style lang="scss" scoped>
  // stylelint-disable declaration-no-important
  .configurations-table-header {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;

    &__top-row {
      display: flex;
      align-items: center;
      width: 100%;
    }

    &__right-actions {
      display: flex;
      align-items: center;
      gap: 16px;
      margin-left: auto;
    }

    &__master-checkbox {
      display: flex;
      align-items: center;
      margin-top: 8px;
    }

    &__master-control {
      display: flex;
      align-items: center;
      padding: 12px 15px;
      border-radius: 10% !important;

      .mi-checkbox {
        ::v-deep(.q-checkbox__label),
        ::v-deep(.q-anchor--skip) {
          display: none;
        }

        ::v-deep(.q-checkbox__inner--indeterminate .q-checkbox__bg) {
          background-color: $mi-white !important;
          border-color: $mi-anthracite-800 !important;
          color: $mi-anthracite-800 !important;
        }

        &:hover {
          background-color: $mi-silver-100;
          border-radius: 4px;
        }
      }
    }

    &__master-dropdown {
      min-height: unset !important;
      padding: 0 !important;
      border-radius: 25% !important;
      width: 22px !important;
      height: 22px !important;
      margin-left: 4px;
      display: flex;
      align-items: center;

      ::v-deep(.q-focus-helper) {
        display: none !important;
      }

      &:hover,
      &--active {
        background-color: $mi-silver-100 !important;
        color: $mi-anthracite-800 !important;
      }
    }

    &__action-buttons {
      display: flex;
      gap: 8px;
    }

    &__select-dropdown {
      display: flex;
      flex-direction: column;

      .mi-btn {
        font-family: "Man Europe", sans-serif;
        font-weight: 400;
        font-size: 14px;
        line-height: 150%;
        align-items: start;
        width: 100%;
      }
    }

    &__title {
      font-family: "Man Europe", sans-serif;
      font-weight: 700;
      font-size: 20px;
      line-height: 30px;
      letter-spacing: 0;
      text-transform: capitalize;
    }

    &__pin-filter {
      padding: 4px;

      ::v-deep(.q-focus-helper) {
        display: none !important;
      }
    }
  }
</style>
